import React from 'react';
import validationConfig from '../../validation-config.json';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Card } from '@uhg-abyss/web/ui/Card';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { createColumn, useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { Router } from '@uhg-abyss/web/ui/Router';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { useAuth } from '../../auth';
import { navigate } from '../../common/navigate';
import { AuthInterceptedError } from '../../common/ErrorHandler';
import { PageSpinner } from '../../common/PageSpinner';
import { SharedDialog } from '../../common/SharedDialog';
import { PolicySummary, startSurveySession, searchPoliciesByPolicyIds, searchPoliciesBySearchId } from './homeApi';
import { getPolicyTableRows, filterTableRows, splitPolicyList, PolicyTableRow } from './tableRows';
import { downloadSurveyExcel } from './downloadSurveyExcel';

const Page = styled('div', {
  width: '100%',
  display: 'grid',
  gap: '1rem',
  minHeight: 0,
  overflow: 'hidden',
});

const ExternalMessage = styled('div', {
  display: 'grid',
  gap: '0.5rem',
});

const SearchSection = styled('section', {
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
});

const Section = styled('section', {
  width: '100%',
});

const NoRecordsPanel = styled(Card, {
  width: '100%',
  borderRadius: '16px',
  border: '1px solid #d7b3b3',
  backgroundColor: '#fff8f8',
  padding: '1rem 1.25rem',
  boxShadow: '0 6px 18px rgba(86, 14, 14, 0.06)',
  boxSizing: 'border-box',
});

const TroubleshootingLink = styled('a', {
  color: '#9d1c1c',
  fontWeight: 600,
  textDecoration: 'underline',
  '&:focus-visible': {
    outline: '2px solid #9d1c1c',
    outlineOffset: '2px',
  },
});

const InlineLinkButton = styled('button', {
  appearance: 'none',
  border: 'none',
  background: 'none',
  padding: 0,
  margin: 0,
  color: '$web.semantic.color.text.label.cta.primary',
  textDecoration: 'underline',
  font: 'inherit',
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'left',
  '&:focus-visible': {
    outline: '2px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
    outlineOffset: '2px',
  },
});

const GateMessagePanel = styled(Card, {
  width: '100%',
  borderRadius: '16px',
  border: '1px solid #d6c48e',
  backgroundColor: '#fffaf0',
  padding: '1rem 1.25rem',
  boxShadow: '0 6px 18px rgba(98, 69, 8, 0.06)',
  boxSizing: 'border-box',
});

const ReopenPromptPanel = styled(Card, {
  width: '100%',
  borderRadius: '16px',
  border: '1px solid #d9c487',
  backgroundColor: '#fffaf0',
  padding: '1rem 1.25rem',
  boxShadow: '0 6px 18px rgba(98, 69, 8, 0.08)',
  boxSizing: 'border-box',
  display: 'grid',
  gap: '0.75rem',
});

const ReopenPromptActions = styled('div', {
  display: 'flex',
  gap: '0.5rem',
  flexWrap: 'wrap',
});

const DataTableWrapper = styled('div', {
  width: '100%',
  boxSizing: 'border-box',
  minWidth: 0,
  '& .abyss-data-table-scroll': {
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  '& .abyss-data-table-header-cell-top-container': {
    alignItems: 'center',
  },
  '& .abyss-data-table-header-data': {
    display: 'flex',
    alignItems: 'center',
    minHeight: '1.5rem',
  },
  '& .abyss-data-table-header-actions-container': {
    alignItems: 'center',
    minHeight: '1.5rem',
    flexShrink: 0,
  },
  '& .abyss-data-table-header-sort-arrow-container': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const SearchPanel = styled(Card, {
  width: '100%',
  borderRadius: '16px',
  border: '1px solid #c8d8e6',
  backgroundColor: '$white',
  padding: '1.25rem',
  boxShadow: '0 8px 20px rgba(0, 36, 77, 0.06)',
  overflow: 'hidden',
  boxSizing: 'border-box',
});

const SearchPanelContent = styled('div', {
  display: 'grid',
  gap: '0.5rem',
});

const SearchHeader = styled('div', {
  display: 'grid',
  gap: '0.35rem',
});

const FieldLabel = styled(Text, {
  display: 'block',
  color: '$web.semantic.color.text.content.primary',
  fontWeight: 600,
  lineHeight: 1.2,
});

const SearchErrorBlock = styled('div', {
  display: 'grid',
  gap: '0.15rem',
});

const InputRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'minmax(320px, 1fr) minmax(280px, 0.85fr)',
  gap: '0.75rem',
  alignItems: 'end',
  minWidth: 0,
  transition: 'grid-template-columns 200ms ease',
  '@media (max-width: 980px)': {
    gridTemplateColumns: '1fr',
  },
});

const InputGroup = styled('div', {
  display: 'grid',
  gap: '0.35rem',
  minWidth: 0,
});

const QuickFilterControls = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: '0.5rem',
  alignItems: 'center',
  minWidth: 0,
});

const QuickFilterInput = styled('input', {
  width: '100%',
  minWidth: '12rem',
  height: '2.5rem',
  borderRadius: '8px',
  border: '1px solid #b6c9d9',
  padding: '0 0.75rem',
  fontSize: '0.95rem',
  boxSizing: 'border-box',
  transition: 'width 200ms ease',
  '&:focus-visible': {
    outline: '2px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
    outlineOffset: '1px',
  },
});

const HeaderWithHelp = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.35rem',
  whiteSpace: 'normal',
  lineHeight: 1.2,
  verticalAlign: 'middle',
});

const HelpButton = styled('button', {
  width: '1rem',
  height: '1rem',
  minWidth: '1rem',
  minHeight: '1rem',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  borderRadius: '9999px',
  border: '1px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
  backgroundColor: '$web.semantic.color.surface.interactive.standards.rest.default.primary',
  color: '$web.semantic.color.text.label.cta.primary-alt',
  fontSize: '0.7rem',
  fontWeight: 700,
  lineHeight: 1,
  cursor: 'pointer',
  flexShrink: 0,
  transform: 'translateY(0)',
  '&:hover': {
    backgroundColor: '$web.semantic.color.surface.interactive.standards.hover.default.primary',
  },
  '&:active': {
    backgroundColor: '$web.semantic.color.surface.interactive.standards.active.default.primary',
  },
  '&:focus-visible': {
    outline: '2px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
    outlineOffset: '1px',
  },
});

const PolicyListCellText = styled('span', {
  display: 'block',
  flex: '1 1 auto',
  maxWidth: '100%',
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const PolicyListCell = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

const PolicyListOverflowTrigger = styled('button', {
  appearance: 'none',
  border: 'none',
  background: 'none',
  padding: 0,
  margin: 0,
  flexShrink: 0,
  color: '$web.semantic.color.text.label.cta.primary',
  textDecoration: 'underline',
  font: 'inherit',
  cursor: 'pointer',
  '&:focus-visible': {
    outline: '2px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
    outlineOffset: '1px',
  },
});

const PAGE_SIZE_OPTIONS = [5, 10, 20];
const FALLBACK_VALUE = 'N/A';
const DEFAULT_STATUS = 'Not Started';
const HOME_INTERNAL_SEARCH_KEY = 'rfi.internalSearchContext';
const HOME_VALIDATION = {
  searchRequiredMessage:
    validationConfig.HOME_PAGE?.search?.requiredMessage ?? 'Enter a search term before searching.',
  quickFilterMinApplyLength:
    validationConfig.HOME_PAGE?.quickFilter?.minApplyLength ?? 3,
  downloadInvalidSurveyMessage:
    validationConfig.HOME_PAGE?.download?.invalidSurveyMessage
    ?? 'Unable to download: missing survey information for this row.',
};

const RFI_CLOSED_DIALOG_TITLE = 'RFI Closed';
const RFI_CLOSED_DIALOG_CONTENT = (
  <React.Fragment>
    <div>RFI submission period has ended. UnitedHealthcare is no longer accepting submissions.</div>
    <div>
      An RFI Status of <strong>In Process</strong> or <strong>Open</strong> are considered
      incomplete and will not be included in the submission to CMS.
    </div>
    <div>
      If the RFI Status is <strong>Complete</strong>, a copy of the submission can be
      downloaded by selecting the <strong>Download</strong> link in the Download{' '}
      RFI column.
    </div>
  </React.Fragment>
);

interface ReopenPendingContext {
  customerNumber: string;
  employerGroupName: string;
  employerGroupEIN: string | null;
  policyNumber: string;
  policyList: string;
  fundingType: string;
  surveyStatus: string;
  surveyYear: string;
}

interface InternalSearchContext {
  searchKey?: string | null;
  quickFilter?: string;
  selectedCustomerNumber?: string;
  selectedPolicyNumber?: string;
  fromSurvey?: boolean;
  returnAction?: 'cancel' | 'save';
}

const TABLE_HORIZONTAL_PADDING = 24;
const COLLAPSED_INPUT_MIN_CH = 10;
const COLLAPSED_INPUT_MAX_CH = 28;
const CONTROL_BUTTON_TRACK = '7rem';
const CONTROL_GAP_TRACK = '0.5rem';
const COLLAPSED_GROUP_MIN_WIDTH = '19rem';

const COLUMN_LAYOUT = {
  employerGroupName: { min: 150, max: 320, flex: 2.2 },
  policyList: { min: 90, max: 200, flex: 1 },
  fundingType: { min: 90, max: 140, flex: 1 },
  submitterName: { min: 115, max: 180, flex: 1.45 },
  dateStarted: { min: 175, max: 230, flex: 1.3 },
  dateSubmitted: { min: 175, max: 230, flex: 1.3 },
  rfiStatus: { min: 95, max: 145, flex: 1 },
  downloadRfi: { min: 95, max: 165, flex: 1 },
} as const;

const RFI_STATUS_HELP_TEXT = (
  <React.Fragment>
    <div>
      <strong>Open:</strong> RFI is not started.
    </div>
    <div>
      <strong>In Process:</strong> RFI is started, but responses have not been submitted to
      UnitedHealthcare. RFIs with status In Process are considered incomplete and will not be
      included in the submission to CMS.
    </div>
    <div>
      <strong>Complete:</strong> RFI has been successfully submitted to UnitedHealthcare.
    </div>
    <div>Note: Responses can be modified until the data collection period ends.</div>
  </React.Fragment>
);

const COLUMN_HELP_TEXT = {
  submitterName: 'This is the name of the individual that submitted the RFI.',
  dateStarted: 'This is the date the RFI was started.',
  dateSubmitted: 'This is the date the RFI was submitted to UnitedHealthcare.',
  rfiStatus: RFI_STATUS_HELP_TEXT,
  downloadRfi: 'Click Download to generate a form containing the current year\'s RFI responses.',
};

function getCompactCh(value: string, fallback: number): number {
  const length = (value || '').length;
  return Math.max(
    COLLAPSED_INPUT_MIN_CH,
    Math.min(COLLAPSED_INPUT_MAX_CH, length > 0 ? length + 2 : fallback),
  );
}

function navigateTo(path: string) {
  navigate(path);
}

function readInternalSearchContext(): InternalSearchContext | null {
  const raw = window.sessionStorage.getItem(HOME_INTERNAL_SEARCH_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as InternalSearchContext;
  } catch {
    return null;
  }
}

function writeInternalSearchContext(nextContext: InternalSearchContext) {
  window.sessionStorage.setItem(HOME_INTERNAL_SEARCH_KEY, JSON.stringify(nextContext));
}

function getColumnHeader(label: string, helpText?: React.ReactNode) {
  if (!helpText) {
    return label;
  }

  return (
    <HeaderWithHelp>
      <span>{label}</span>
      <Tooltip
        content={helpText}
        position="top"
        variant="gray"
        delayDuration={200}
      >
        <HelpButton
          aria-label={`${label} information`}
          type="button"
        >
          ?
        </HelpButton>
      </Tooltip>
    </HeaderWithHelp>
  );
}

function renderPolicyListCell(row: PolicyTableRow) {
  const values = splitPolicyList(row.policyList, FALLBACK_VALUE);

  if (values.length <= 2) {
    return (
      <PolicyListCell>
        <PolicyListCellText>{row.policyList}</PolicyListCellText>
      </PolicyListCell>
    );
  }

  const visibleValues = values.slice(0, 2).join(', ');
  const remainingValues = values.slice(2);

  return (
    <PolicyListCell>
      <PolicyListCellText>{visibleValues}</PolicyListCellText>
      <Tooltip
        content={
          <React.Fragment>
            <div>{remainingValues.length} more value(s):</div>
            <div>{remainingValues.join(', ')}</div>
          </React.Fragment>
        }
        position="top"
        variant="gray"
        delayDuration={200}
      >
        <PolicyListOverflowTrigger
          aria-label={`Show ${remainingValues.length} additional policy value(s)`}
          type="button"
        >
        ..
        </PolicyListOverflowTrigger>
      </Tooltip>
    </PolicyListCell>
  );
}

function getResponsiveColumnSizes(containerWidth: number) {
  const availableWidth = Math.max(containerWidth - TABLE_HORIZONTAL_PADDING, 0);
  const entries = Object.entries(COLUMN_LAYOUT);
  const minimumWidth = entries.reduce((sum, [, config]) => {return sum + config.min}, 0);

  if (availableWidth <= minimumWidth) {
    return Object.fromEntries(entries.map(([key, config]) => {return [key, config.min]}));
  }

  let remainingWidth = availableWidth - minimumWidth;
  const sizes: Record<string, number> = Object.fromEntries(
    entries.map(([key, config]) => {return [key, config.min]}),
  );
  let expandable = entries.filter(([, config]) => {return config.max > config.min});

  while (remainingWidth > 0 && expandable.length > 0) {
    const totalFlex = expandable.reduce((sum, [, config]) => {return sum + config.flex}, 0);
    let distributed = 0;

    expandable.forEach(([key, config]) => {
      const current = sizes[key] as number;
      const room = config.max - current;

      if (room <= 0) {
        return;
      }

      const share = remainingWidth * (config.flex / totalFlex);
      const increment = Math.min(room, Math.max(1, Math.floor(share)));

      sizes[key] = current + increment;
      distributed += increment;
    });

    if (distributed === 0) {
      break;
    }

    remainingWidth -= distributed;
    expandable = expandable.filter(([key, config]) => {return (sizes[key] as number) < config.max});
  }

  if (remainingWidth > 0) {
    sizes.submitterName = (sizes.submitterName as number) + remainingWidth;
  }

  return sizes as Record<keyof typeof COLUMN_LAYOUT, number>;
}

function getPolicyTableColumns(
  containerWidth: number,
  onSelectCustomer: (row: PolicyTableRow) => void,
  onDownloadRow: (row: PolicyTableRow) => void,
  downloadingRows: Set<string>,
) {
  const columnSizes = getResponsiveColumnSizes(containerWidth);

  return [
    createColumn<PolicyTableRow>({
      id: 'employerGroupName',
      accessorKey: 'employerGroupName',
      header: 'Employer Group Name',
      cell: (context: { row: { original: PolicyTableRow } }) => {return (
        <InlineLinkButton
          type="button"
          onClick={() => {
            onSelectCustomer(context.row.original);
          }}
        >
          {context.row.original.employerGroupName}
        </InlineLinkButton>
      )},
      size: columnSizes.employerGroupName,
      minSize: COLUMN_LAYOUT.employerGroupName.min,
      maxSize: COLUMN_LAYOUT.employerGroupName.max,
      enableSorting: true,
    }),
    createColumn<PolicyTableRow>({
      id: 'policyList',
      accessorKey: 'policyList',
      header: 'Policy List',
      cell: (context: { row: { original: PolicyTableRow } }) => {return renderPolicyListCell(context.row.original)},
      size: columnSizes.policyList,
      minSize: COLUMN_LAYOUT.policyList.min,
      maxSize: COLUMN_LAYOUT.policyList.max,
      enableSorting: true,
    }),
    createColumn<PolicyTableRow>({
      id: 'fundingType',
      accessorKey: 'fundingType',
      header: 'Funding Type',
      size: columnSizes.fundingType,
      minSize: COLUMN_LAYOUT.fundingType.min,
      maxSize: COLUMN_LAYOUT.fundingType.max,
      enableSorting: true,
    }),
    createColumn<PolicyTableRow>({
      id: 'submitterName',
      accessorKey: 'submitterName',
      header: () => {return getColumnHeader('Submitter Name', COLUMN_HELP_TEXT.submitterName)},
      meta: { headerLabel: 'Submitter Name' },
      size: columnSizes.submitterName,
      minSize: COLUMN_LAYOUT.submitterName.min,
      maxSize: COLUMN_LAYOUT.submitterName.max,
      enableSorting: true,
    }),
    createColumn<PolicyTableRow>({
      id: 'dateStarted',
      accessorKey: 'dateStarted',
      header: () => {return getColumnHeader('Date Started', COLUMN_HELP_TEXT.dateStarted)},
      meta: { headerLabel: 'Date Started' },
      size: columnSizes.dateStarted,
      minSize: COLUMN_LAYOUT.dateStarted.min,
      maxSize: COLUMN_LAYOUT.dateStarted.max,
      enableSorting: true,
    }),
    createColumn<PolicyTableRow>({
      id: 'dateSubmitted',
      accessorKey: 'dateSubmitted',
      header: () => {return getColumnHeader('Date Submitted', COLUMN_HELP_TEXT.dateSubmitted)},
      meta: { headerLabel: 'Date Submitted' },
      size: columnSizes.dateSubmitted,
      minSize: COLUMN_LAYOUT.dateSubmitted.min,
      maxSize: COLUMN_LAYOUT.dateSubmitted.max,
      enableSorting: true,
    }),
    createColumn<PolicyTableRow>({
      id: 'rfiStatus',
      accessorKey: 'rfiStatus',
      header: () => {return getColumnHeader('RFI Status', COLUMN_HELP_TEXT.rfiStatus)},
      meta: { headerLabel: 'RFI Status' },
      size: columnSizes.rfiStatus,
      minSize: COLUMN_LAYOUT.rfiStatus.min,
      maxSize: COLUMN_LAYOUT.rfiStatus.max,
      enableSorting: true,
    }),
    createColumn<PolicyTableRow>({
      id: 'downloadRfi',
      accessorKey: 'downloadRfi',
      header: () => {return getColumnHeader('Download RFI', COLUMN_HELP_TEXT.downloadRfi)},
      meta: { headerLabel: 'Download RFI' },
      cell: (context: { row: { original: PolicyTableRow } }) => {
        const row = context.row.original;
        if (row.downloadRfi !== 'Download') {
          return null;
        }
        const isDownloading = downloadingRows.has(row.uniqueId);
        return (
          <InlineLinkButton
            type="button"
            disabled={isDownloading}
            aria-label={`Download RFI for ${row.employerGroupName}`}
            onClick={() => {
              onDownloadRow(row);
            }}
          >
            {isDownloading ? 'Downloading...' : 'Download'}
          </InlineLinkButton>
        );
      },
      size: columnSizes.downloadRfi,
      minSize: COLUMN_LAYOUT.downloadRfi.min,
      maxSize: COLUMN_LAYOUT.downloadRfi.max,
      enableSorting: false,
    }),
  ];
}

export const Home = () => {
  const { session } = useAuth();
  const restoredFromSurveyRef = React.useRef(false);
  const externalBootstrapDoneRef = React.useRef(false);
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);
  const quickFilterInputRef = React.useRef<HTMLInputElement | null>(null);
  const [activeField, setActiveField] = React.useState<'search' | 'filter' | null>(null);
  const [searchKey, setSearchKey] = React.useState('');
  const [quickFilter, setQuickFilter] = React.useState('');
  const [submittedSearchKey, setSubmittedSearchKey] = React.useState<string | null>(null);
  const [isSearching, setIsSearching] = React.useState(false);
  const [searchError, setSearchError] = React.useState<string | null>(null);
  const [results, setResults] = React.useState<PolicySummary[]>([]);
  const [lastTraceId, setLastTraceId] = React.useState<string | null>(null);
  const [surveyGateMessage, setSurveyGateMessage] = React.useState<string | null>(null);
  const [isClosedSurveyDialogOpen, setIsClosedSurveyDialogOpen] = React.useState(false);
  const [returnMessage, setReturnMessage] = React.useState<string | null>(null);
  const [reopenPromptMessage, setReopenPromptMessage] = React.useState<string | null>(null);
  const [reopenPendingContext, setReopenPendingContext] = React.useState<ReopenPendingContext | null>(null);
  const [isSubmittingReopen, setIsSubmittingReopen] = React.useState(false);
  const [tableContainerWidth, setTableContainerWidth] = React.useState(1040);
  const [downloadingRows, setDownloadingRows] = React.useState<Set<string>>(new Set());
  const [downloadError, setDownloadError] = React.useState<string | null>(null);

  const isInternalUser = session?.authType === 'INTERNAL';

  const tableRows = React.useMemo(
    () => {return getPolicyTableRows(results, FALLBACK_VALUE, DEFAULT_STATUS)},
    [results],
  );

  const displayedResults = React.useMemo(
    () => {return filterTableRows(tableRows, quickFilter, HOME_VALIDATION.quickFilterMinApplyLength)},
    [quickFilter, tableRows],
  );

  React.useEffect(() => {
    if (returnMessage && displayedResults.length > 0) {
      setReturnMessage(null);
    }
  }, [displayedResults.length, returnMessage]);

  const applySurveyDecision = React.useCallback((decision: Awaited<ReturnType<typeof startSurveySession>>) => {
    if (decision.requiresReopenConfirmation) {
      setReopenPromptMessage(
        decision.message ?? 'Do you want to re-open this customer RFI ?',
      );
      setReopenPendingContext({
        customerNumber: decision.selectedCustomer.customerNumber,
        employerGroupName: decision.selectedCustomer.employerGroupName,
        employerGroupEIN: decision.selectedCustomer.employerGroupEIN,
        policyNumber: decision.selectedCustomer.policyNumber,
        policyList: decision.selectedCustomer.policyList,
        fundingType: decision.selectedCustomer.fundingType,
        surveyStatus: decision.selectedCustomer.surveyStatus,
        surveyYear: decision.selectedCustomer.surveyYear,
      });
      return;
    }

    if (!decision.allowed || decision.reasonCode === 'ACTIVE_SURVEY_UNAVAILABLE') {
      setSurveyGateMessage(null);
      setIsClosedSurveyDialogOpen(true);
      return;
    }

    if (!decision.navigationPath || !decision.activeSurvey) {
      setSurveyGateMessage(
        decision.message ?? 'The survey collection period is closed. Customer responses cannot be opened right now.',
      );
      return;
    }

    setIsClosedSurveyDialogOpen(false);
    window.sessionStorage.setItem(
      HOME_INTERNAL_SEARCH_KEY,
      JSON.stringify({
        searchKey: submittedSearchKey,
        quickFilter,
        selectedCustomerNumber: decision.selectedCustomer.customerNumber,
        selectedPolicyNumber: decision.selectedCustomer.policyNumber,
      }),
    );

    navigateTo(decision.navigationPath);
  }, [quickFilter, submittedSearchKey]);

  const clearReopenPrompt = React.useCallback(() => {
    setReopenPromptMessage(null);
    setReopenPendingContext(null);
    setIsSubmittingReopen(false);
  }, []);

  const confirmReopenAndContinue = React.useCallback(async () => {
    if (!session?.token || !reopenPendingContext) {
      setSurveyGateMessage('No authenticated session found. Please sign in again.');
      clearReopenPrompt();
      return;
    }

    setIsSubmittingReopen(true);
    setSurveyGateMessage(null);

    try {
      const decision = await startSurveySession({
        customerNumber: reopenPendingContext.customerNumber,
        employerGroupName: reopenPendingContext.employerGroupName,
        employerGroupEIN: reopenPendingContext.employerGroupEIN,
        policyNumber: reopenPendingContext.policyNumber,
        policyList: reopenPendingContext.policyList,
        fundingType: reopenPendingContext.fundingType,
        surveyStatus: reopenPendingContext.surveyStatus,
        surveyYear: reopenPendingContext.surveyYear,
        confirmReopen: true,
      });

      clearReopenPrompt();
      applySurveyDecision(decision);
    } catch (error) {
      if (error instanceof AuthInterceptedError) return;
      setSurveyGateMessage(
        error instanceof Error ? error.message : 'Unable to re-open and start the survey workflow.',
      );
      setIsSubmittingReopen(false);
    }
  }, [applySurveyDecision, clearReopenPrompt, reopenPendingContext, session?.token]);

  const handleSelectCustomer = React.useCallback(async (row: PolicyTableRow) => {
    if (!session?.token) {
      setSurveyGateMessage('No authenticated session found. Please sign in again.');
      return;
    }

    clearReopenPrompt();
    setIsClosedSurveyDialogOpen(false);
    setSurveyGateMessage(null);

    let decision;

    try {
      decision = await startSurveySession({
        customerNumber: row.rawCustomerNumber,
        employerGroupName: row.employerGroupName,
        employerGroupEIN: row.rawEmployerGroupEIN,
        policyNumber: row.rawPolicyNumber,
        policyList: row.policyList,
        fundingType: row.fundingType,
        surveyStatus: row.rfiStatus,
        surveyYear: row.rawSurveyYear,
      });
    } catch (error) {
      if (error instanceof AuthInterceptedError) return;
      setSurveyGateMessage(
        error instanceof Error ? error.message : 'Unable to start the survey workflow.',
      );
      return;
    }

    applySurveyDecision(decision);
  }, [applySurveyDecision, clearReopenPrompt, session?.token]);

  const handleDownloadRow = React.useCallback(async (row: PolicyTableRow) => {
    if (!session?.token) {
      setDownloadError('No authenticated session found. Please sign in again.');
      return;
    }

    const surveyId = parseInt(row.rawSurveyId, 10);
    const reportingYear = parseInt(row.rawSurveyYear, 10);

    if (isNaN(surveyId) || isNaN(reportingYear)) {
      setDownloadError(HOME_VALIDATION.downloadInvalidSurveyMessage);
      return;
    }

    setDownloadError(null);
    setDownloadingRows((prev) => {return new Set([...prev, row.uniqueId])});

    try {
      await downloadSurveyExcel({
        customerNumber: row.rawCustomerNumber,
        fundingType: row.fundingType,
        reportingYear,
        surveyId,
        employerGroupName: row.employerGroupName,
        surveyYear: row.rawSurveyYear,
      });
    } catch (error) {
      if (error instanceof AuthInterceptedError) return;
      setDownloadError(
        error instanceof Error ? error.message : 'Unable to download the survey response.',
      );
    } finally {
      setDownloadingRows((prev) => {
        const next = new Set(prev);
        next.delete(row.uniqueId);
        return next;
      });
    }
  }, [session?.token]);

  const tableColumns = React.useMemo(
    () => {return getPolicyTableColumns(tableContainerWidth, handleSelectCustomer, handleDownloadRow, downloadingRows)},
    [handleSelectCustomer, handleDownloadRow, downloadingRows, tableContainerWidth],
  );

  const inputRowCss = React.useMemo(() => {
    const searchCh = getCompactCh(searchKey, 12);
    const filterCh = getCompactCh(quickFilter, 14);
    const searchCollapsedTrack = `max(${COLLAPSED_GROUP_MIN_WIDTH}, calc(${searchCh}ch + ${CONTROL_BUTTON_TRACK} + ${CONTROL_GAP_TRACK}))`;
    const filterCollapsedTrack = `max(${COLLAPSED_GROUP_MIN_WIDTH}, calc(${filterCh}ch + ${CONTROL_BUTTON_TRACK} + ${CONTROL_GAP_TRACK}))`;

    if (activeField === 'search') {
      return {
        gridTemplateColumns: `minmax(0,1fr) ${filterCollapsedTrack}`,
      };
    }

    if (activeField === 'filter') {
      return {
        gridTemplateColumns: `${searchCollapsedTrack} minmax(0,1fr)`,
      };
    }

    return undefined;
  }, [activeField, searchKey, quickFilter]);

  const tableState = useDataTable<PolicyTableRow>({
    initialData: tableRows,
    initialColumns: tableColumns,
    rowIdKey: 'uniqueId',
    tableConfig: {
      enableSorting: true,
      enableColumnResizing: true,
      columnResizeMode: 'onEnd',
    },
    defaultSettingsConfig: {
      headerDisplaySettings: {
        hideSortingButton: false,
      },
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
    },
  });

  React.useEffect(() => {
    tableState.setData(displayedResults);
  }, [displayedResults]);

  React.useEffect(() => {
    tableState.setColumns(tableColumns);
  }, [tableColumns]);

  React.useEffect(() => {
    const container = tableContainerRef.current;

    if (!container) {
      return;
    }

    const updateWidth = () => {
      setTableContainerWidth(container.getBoundingClientRect().width);
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  React.useEffect(() => {
    if (quickFilter.trim().length >= HOME_VALIDATION.quickFilterMinApplyLength && displayedResults.length === 0) {
      quickFilterInputRef.current?.focus();
    }
  }, [quickFilter, displayedResults.length]);

  const onSearch = async (value: string) => {
    if (isSearching) {
      return;
    }

    const nextSearchKey = value.trim();
    setSubmittedSearchKey(nextSearchKey || null);

    if (!nextSearchKey) {
      setSearchError(HOME_VALIDATION.searchRequiredMessage);
      setResults([]);
      return;
    }

    if (!session?.token) {
      setSearchError('No authenticated session found. Please sign in again.');
      setResults([]);
      return;
    }

    setSearchError(null);
    setIsSearching(true);
    setLastTraceId(null);

    try {
      const { data, traceId } = await searchPoliciesBySearchId({
        searchId: nextSearchKey,
      });
      setResults(data);
      setQuickFilter('');
      setLastTraceId(traceId);
      setSurveyGateMessage(null);
      clearReopenPrompt();
    } catch (error) {
      if (error instanceof AuthInterceptedError) return;
      setResults([]);
      setLastTraceId(
        typeof error === 'object' && error !== null && 'traceId' in error && typeof error.traceId === 'string'
          ? error.traceId
          : null,
      );
      setSearchError(
        error instanceof Error ? error.message : 'Unable to load policy search results.',
      );
    } finally {
      setIsSearching(false);
    }
  };

  React.useEffect(() => {
    if (!isInternalUser || !session?.token || restoredFromSurveyRef.current) {
      return;
    }

    const searchContext = readInternalSearchContext();
    const restoredSearchKey = searchContext?.searchKey?.trim();
    if (!searchContext?.fromSurvey || !restoredSearchKey) {
      return;
    }

    restoredFromSurveyRef.current = true;
    setSearchKey(restoredSearchKey);
    setReturnMessage(
      searchContext.returnAction === 'save'
        ? 'Survey responses saved. Search results restored.'
        : 'Returned from survey. Search results restored.',
    );

    void (async () => {
      await onSearch(restoredSearchKey);
      setQuickFilter(searchContext.quickFilter ?? '');
      writeInternalSearchContext({
        ...searchContext,
        fromSurvey: false,
      });
    })();
  }, [isInternalUser, onSearch, session?.token]);

  React.useEffect(() => {
    if (isInternalUser || !session?.token || externalBootstrapDoneRef.current) {
      return;
    }

    const policyIds = session.externalContext?.policyIds ?? [];
    externalBootstrapDoneRef.current = true;

    if (!policyIds.length) {
      setResults([]);
      setSubmittedSearchKey('External policy bootstrap');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setSubmittedSearchKey('External policy bootstrap');

    void (async () => {
      try {
        const { data, traceId } = await searchPoliciesByPolicyIds({
          policyIds,
          portal: session.externalContext?.portal,
        });
        setResults(data);
        setLastTraceId(traceId);
      } catch (error) {
        if (error instanceof AuthInterceptedError) return;
        setResults([]);
        setSearchError(error instanceof Error ? error.message : 'Unable to load external policy bootstrap results.');
      } finally {
        setIsSearching(false);
      }
    })();
  }, [isInternalUser, session?.externalContext?.policyIds, session?.token]);

  const hasSearchInput = searchKey.trim().length > 0;
  const isExternalUser = !isInternalUser;
  const isExternalNoPoliciesState = isExternalUser && !isSearching && !searchError && submittedSearchKey !== null && results.length === 0;
  const shouldRenderSharedHomeShell = isInternalUser || isExternalNoPoliciesState;
  const disableSearchControls = isSearching || isExternalNoPoliciesState;
  const canRunSearch = isInternalUser && hasSearchInput && !isSearching;
  const hasQuickFilterInput = quickFilter.trim().length > 0;
  const canClearQuickFilter = results.length > 0 && hasQuickFilterInput;

  return (
    <React.Fragment>
      <Router.MetaTags title="Home" />
      {isSearching ? <PageSpinner label="Loading Home search results" blocking /> : null}
      <Page>
        {!shouldRenderSharedHomeShell ? (
          <ExternalMessage>
            <Text>
              External sign-in completed. Loading policies for your account.
            </Text>

            {searchError ? (
              <SearchErrorBlock>
                <Text color="$web.semantic.color.text.content.error">{searchError}</Text>
                {lastTraceId ? (
                  <Text css={{ fontSize: '0.75rem', color: '$web.semantic.color.text.content.secondary' }}>
                    Reference ID: {lastTraceId}
                  </Text>
                ) : null}
              </SearchErrorBlock>
            ) : null}

            {!isSearching && displayedResults.length > 0 ? (
              <Section>
                <Heading
                  level={4}
                  size="sm"
                  color="$web.semantic.color.text.content.primary"
                  textAlign="left"
                >
                  Search Results ({displayedResults.length})
                </Heading>
                <DataTableWrapper ref={tableContainerRef}>
                  <DataTable<PolicyTableRow>
                    title={`Search Results (${displayedResults.length})`}
                    hideHeader
                    tableState={tableState}
                  >
                    <DataTable.Pagination />
                    <DataTable.Table />
                  </DataTable>
                </DataTableWrapper>
              </Section>
            ) : null}

            {!isSearching && !searchError && submittedSearchKey && results.length === 0 ? (
              <Section>
                <NoRecordsPanel>
                  <Text color="$web.semantic.color.text.content.error">
                    No policies found for your external account.
                  </Text>
                  <Text>
                    Please <TroubleshootingLink href="/rfiinfo#troubleshooting">click here</TroubleshootingLink> to troubleshoot the issue.
                  </Text>
                </NoRecordsPanel>
              </Section>
            ) : null}
          </ExternalMessage>
        ) : (
          <React.Fragment>
            <SearchSection>
              <SearchPanel>
                <SearchPanelContent>
                  <SearchHeader>
                    <Heading
                      level={3}
                      size="md"
                      color="$web.semantic.color.text.content.primary"
                      textAlign="left"
                    >
                      Customer Search
                    </Heading>
                  </SearchHeader>

                  <InputRow css={inputRowCss}>
                    <InputGroup
                      onFocusCapture={() => {
                        setActiveField('search');
                      }}
                      onBlurCapture={(event) => {
                        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                          setActiveField((prev) => {return (prev === 'search' ? null : prev)});
                        }
                      }}
                    >
                      <FieldLabel>Search key</FieldLabel>
                      <QuickFilterControls>
                        <QuickFilterInput
                          type="text"
                          value={searchKey}
                          disabled={disableSearchControls}
                          onChange={(event) => {
                            setSearchKey(event.target.value);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' && isInternalUser && !isSearching) {
                              event.preventDefault();
                              void onSearch(searchKey);
                            }
                          }}
                          placeholder="Search by customer number, employer group name, or policy number"
                          aria-label="Search key"
                        />
                        <Button
                          type="button"
                          variant={canRunSearch ? 'filled' : 'outline'}
                          size="sm"
                          disabled={!canRunSearch}
                          onClick={() => {
                            void onSearch(searchKey);
                          }}
                        >
                          Search
                        </Button>
                      </QuickFilterControls>
                    </InputGroup>

                    <InputGroup
                      onFocusCapture={() => {
                        setActiveField('filter');
                      }}
                      onBlurCapture={(event) => {
                        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                          setActiveField((prev) => {return (prev === 'filter' ? null : prev)});
                        }
                      }}
                    >
                      <FieldLabel>Quick filter</FieldLabel>
                      <QuickFilterControls>
                        <QuickFilterInput
                          ref={quickFilterInputRef}
                          type="text"
                          value={quickFilter}
                          disabled={results.length === 0 || isSearching}
                          onChange={(event) => {
                            setQuickFilter(event.target.value);
                          }}
                          placeholder={`Filter loaded results (applies after ${HOME_VALIDATION.quickFilterMinApplyLength}+ characters)`}
                          aria-label="Quick filter results"
                        />
                        <Button
                          type="button"
                          variant={canClearQuickFilter ? 'filled' : 'outline'}
                          size="sm"
                          disabled={!canClearQuickFilter}
                          onClick={() => {
                            setQuickFilter('');
                          }}
                        >
                          Clear
                        </Button>
                      </QuickFilterControls>
                    </InputGroup>
                  </InputRow>

                  {submittedSearchKey ? (
                    isInternalUser ? (
                      <Text>
                        Submitted search term: <strong>{submittedSearchKey}</strong>
                      </Text>
                    ) : null
                  ) : isInternalUser ? (
                    <Text>Enter a search term and select Search to retrieve matching policies.</Text>
                  ) : null}
                  {isInternalUser && searchError ? (
                    <SearchErrorBlock>
                      <Text color="$web.semantic.color.text.content.error">{searchError}</Text>
                      {lastTraceId ? (
                        <Text css={{ fontSize: '0.75rem', color: '$web.semantic.color.text.content.secondary' }}>
                          Reference ID: {lastTraceId}
                        </Text>
                      ) : null}
                    </SearchErrorBlock>
                  ) : null}

                  {isInternalUser && !isSearching && !searchError && quickFilter.trim().length >= HOME_VALIDATION.quickFilterMinApplyLength && results.length > 0 ? (
                    <Text>{displayedResults.length} records found</Text>
                  ) : null}

                  {surveyGateMessage ? (
                    <GateMessagePanel>
                      <Text color="$web.semantic.color.text.content.warning">{surveyGateMessage}</Text>
                    </GateMessagePanel>
                  ) : null}

                  {returnMessage ? (
                    <GateMessagePanel>
                      <Text>{returnMessage}</Text>
                    </GateMessagePanel>
                  ) : null}

                  {downloadError ? (
                    <GateMessagePanel>
                      <Text color="$web.semantic.color.text.content.error">{downloadError}</Text>
                    </GateMessagePanel>
                  ) : null}

                  <SharedDialog
                    isOpen={isClosedSurveyDialogOpen}
                    onClose={() => {
                      setIsClosedSurveyDialogOpen(false);
                    }}
                    mode="app"
                    title={RFI_CLOSED_DIALOG_TITLE}
                    ariaLabel={RFI_CLOSED_DIALOG_TITLE}
                    primaryAction={{
                      label: 'Dismiss',
                      onClick: () => {
                        setIsClosedSurveyDialogOpen(false);
                      },
                    }}
                  >
                    {RFI_CLOSED_DIALOG_CONTENT}
                  </SharedDialog>

                  {reopenPromptMessage ? (
                    <ReopenPromptPanel>
                      <Text>{reopenPromptMessage}</Text>
                      <ReopenPromptActions>
                        <Button
                          type="button"
                          variant="filled"
                          size="sm"
                          onClick={() => {
                            void confirmReopenAndContinue();
                          }}
                          disabled={isSubmittingReopen}
                        >
                          {isSubmittingReopen ? 'Continuing...' : 'Yes, Re-open and Continue'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            clearReopenPrompt();
                          }}
                          disabled={isSubmittingReopen}
                        >
                          Cancel
                        </Button>
                      </ReopenPromptActions>
                    </ReopenPromptPanel>
                  ) : null}
                </SearchPanelContent>
              </SearchPanel>
            </SearchSection>

            {!isSearching && displayedResults.length > 0 ? (
              <Section>
                <Heading
                  level={4}
                  size="sm"
                  color="$web.semantic.color.text.content.primary"
                  textAlign="left"
                >
                  Search Results ({displayedResults.length})
                </Heading>
                <DataTableWrapper ref={tableContainerRef}>
                  <DataTable<PolicyTableRow>
                    title={`Search Results (${displayedResults.length})`}
                    hideHeader
                    tableState={tableState}
                  >
                    <DataTable.Pagination />
                    <DataTable.Table />
                  </DataTable>
                </DataTableWrapper>
              </Section>
            ) : null}

            {!isSearching && !searchError && submittedSearchKey && results.length === 0 ? (
              <Section>
                <NoRecordsPanel>
                  <Text color="$web.semantic.color.text.content.error">
                    {isInternalUser ? 'No policies found for the provided search term.' : 'No policies found for your external account.'}
                  </Text>
                  <Text>
                    Please <TroubleshootingLink href="/rfiinfo#troubleshooting">click here</TroubleshootingLink> to troubleshoot the issue.
                  </Text>
                </NoRecordsPanel>
              </Section>
            ) : null}
          </React.Fragment>
        )}
      </Page>
    </React.Fragment>
  );
};
